import { ActionButton, Button } from '@library/components/buttons';
import type { DateObjectUnits, DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { Float } from '@headlessui-float/react';
import { Popover } from '@headlessui/react';
import { TimePicker } from '@library/components/datePicker/TimePicker';
import { TimePickerContext } from '@library/components/datePicker/TimePickerContext';
import addIcon from '@library/assets/images/icons/add-icon.svg';
import { classNames } from '@library/utils';
import clockIcon from '@library/assets/images/icons/clock-icon.svg';
import { getSplitTime } from '@library/component-utils/dateTime';
import { useTimePickerContext } from '@library/hooks/useTimePickerContext';

const DELAY_PRESETS = [1, 5, 8, 10, 15, 20];

export interface AdjustDelayPopoverProps {
  initialStartTime: DateTime;
  updatedStartTime: DateTime;
  onUpdateStartTime: (newStartTime: DateTime) => void;
  onSaveDelay: (delay: number, isTimePickerChange: boolean) => void;
}

export const AdjustDelayPopover = ({
  initialStartTime,
  updatedStartTime,
  onUpdateStartTime,
  onSaveDelay
}: AdjustDelayPopoverProps) => {
  const [isTimePickerChange, setIsTimePickerChange] = useState(false);

  const handleTimePickerChange = (newTime: DateObjectUnits) => {
    const timeWithNewHours = updatedStartTime.set(newTime);
    setIsTimePickerChange(true);
    onUpdateStartTime(timeWithNewHours);
  };

  const [delay, dayChange] = useMemo(() => {
    const calculatedDelay = updatedStartTime.diff(initialStartTime, 'minutes');

    const didDayChange = !updatedStartTime.hasSame(initialStartTime, 'day');
    return [calculatedDelay.minutes, didDayChange];
  }, [initialStartTime, updatedStartTime]);

  const timePickerContext = useTimePickerContext({
    date: updatedStartTime,
    initialDate: updatedStartTime,
    minDate: updatedStartTime.minus({ minutes: 8640 }),
    maxDate: updatedStartTime.plus({ minutes: 8640 }),
    onChange: handleTimePickerChange
  });

  const handleAddTime = (amount: number) => {
    setIsTimePickerChange(false);
    const newTime = updatedStartTime.plus({ minutes: amount });
    onUpdateStartTime(newTime);

    const externalSplitTime = getSplitTime(newTime);

    timePickerContext.setDisplayValues({
      amPm: '',
      hour:
        externalSplitTime.hour !== timePickerContext.placeholderSplitTime.hour
          ? externalSplitTime.hour
          : '',
      minutes:
        externalSplitTime.minutes !==
        timePickerContext.placeholderSplitTime.minutes
          ? externalSplitTime.minutes
          : ''
    });

    timePickerContext.setProposedTime(newTime);
  };

  return (
    <Popover>
      {({ close }) => (
        <Float zIndex={9} placement="bottom-start" portal strategy="absolute">
          <Popover.Button
            type="button"
            title="ToggleStartTimeTooltipButton"
            className={classNames('flex h-6 w-6 items-center justify-center')}
            onClick={() => {
              onUpdateStartTime(timePickerContext.initialTime);
              timePickerContext.resetTime();
            }}
          >
            <img alt="Clock icon" src={clockIcon} />
          </Popover.Button>
          <Popover.Panel className="border-soFresh-140 bg-soFresh-110 fixed z-50 mt-1 max-w-80 rounded-md border p-3">
            <div className="text-soClean-100 font-heebo border-soFresh-140 border-b pb-2 text-base font-bold">
              Adjust Delay
            </div>
            {DELAY_PRESETS.map((amount) => (
              <div key={amount} className="flex flex-row justify-between">
                <div className="text-soClean-100 font-heebo py-2 text-sm">
                  {`${amount} minute${amount === 1 ? '' : 's'}`}
                </div>
                <Button
                  title="addTimeButton"
                  onClick={() => handleAddTime(amount)}
                  type="button"
                >
                  <img alt="add icon" className="h-6" src={addIcon} />
                </Button>
              </div>
            ))}
            <div className="text-soClean-100 font-heebo border-soFresh-140 border-t pt-3 text-xs font-semibold">
              Adding time using the presets above will add time to each
              projection&apos;s respective start time. If you wish to change all
              projections to have one singular start time, please use the time
              picker below
            </div>
            <TimePickerContext.Provider value={timePickerContext}>
              <TimePicker />
            </TimePickerContext.Provider>

            <div className="flex flex-row justify-end gap-2">
              {dayChange && (
                <span className="text-roses w-[140px] break-normal text-xs leading-none">
                  Warning: This delay crosses over into the next day
                </span>
              )}
              <ActionButton
                title="clearSaveDelay"
                handleOnClick={() => {
                  onUpdateStartTime(initialStartTime);
                  timePickerContext.resetTime();
                }}
                variant="secondary"
              >
                Clear
              </ActionButton>
              <ActionButton
                disabled={timePickerContext.validations.isError}
                title="saveDelay"
                handleOnClick={() => {
                  onSaveDelay(delay, isTimePickerChange);
                  close();
                }}
                variant="primary"
              >
                Save
              </ActionButton>
            </div>
          </Popover.Panel>
        </Float>
      )}
    </Popover>
  );
};
